import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import { useUnassignPartsOrdersMutation } from '../queries/unassignPartsOrders.mutation.generated';
import { getThirdPartyClient } from '../thirdParty/client';

interface CancelCourierDialogProps {
  open: boolean;
  onClose: () => void;
  partsDeliveryId: number;
  vendorLabel: string;
  /** Orders currently assigned to the run — returned to the pending queue on success. */
  orderIds: number[];
  /** Called after a successful cancel (refresh the courier state / run detail). */
  onSuccess: () => void;
}

/**
 * Confirm-dialog for canceling a booked courier delivery. On confirm:
 * partsDeliveryCancel action (vendor cancel + row write) → unassign the run's
 * orders back to the pending queue (the dealer canceled to re-dispatch) →
 * refresh. A "still being booked / try again in a minute" rejection leaves
 * everything untouched — the dealer just retries.
 */
const CancelCourierDialog: React.FC<CancelCourierDialogProps> = ({
  open,
  onClose,
  partsDeliveryId,
  vendorLabel,
  orderIds,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [unassignOrders] = useUnassignPartsOrdersMutation();
  // Hold a reload only while the vendor cancel and unassign are running.
  useReloadGuard(cancelling, 'Courier cancellation');

  const handleConfirm = async () => {
    setCancelling(true);
    try {
      await getThirdPartyClient().cancel(partsDeliveryId, reason.trim() || undefined);
      // Orders go back to the queue so they can be re-dispatched — best-effort:
      // the delivery is already canceled at the vendor, so a failure here only
      // means the dealer unassigns by hand.
      if (orderIds.length) {
        try {
          await unassignOrders({ variables: { orderIds } });
        } catch (err) {
          console.error('Failed to return orders to the queue after cancel:', err);
          toast.warn('Delivery cancelled, but the orders could not be returned to the queue automatically.');
        }
      }
      toast.success('Courier delivery cancelled — orders returned to the queue');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      toast.error((error as Error).message || 'Failed to cancel the delivery');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onClose={cancelling ? undefined : onClose} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Cancel this courier delivery?</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant='body2' color='text.secondary'>
          This cancels the {vendorLabel} courier for this run. The delivery fee may still apply per the
          courier&apos;s cancellation policy
          {orderIds.length
            ? `, and the run's ${orderIds.length} order${orderIds.length !== 1 ? 's' : ''} will return to the pending queue for re-dispatch.`
            : '.'}
        </Typography>
        <Alert severity='warning' sx={{ py: 0.5 }}>
          This can&apos;t be undone — a new delivery needs a fresh quote.
        </Alert>
        <TextField
          label='Reason (optional)'
          value={reason}
          onChange={e => setReason(e.target.value)}
          size='small'
          fullWidth
          multiline
          minRows={2}
          placeholder='e.g. customer no longer needs the parts today'
          disabled={cancelling}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant='outlined'
          onClick={onClose}
          disabled={cancelling}
          sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
        >
          Keep delivery
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={handleConfirm}
          disabled={cancelling}
          startIcon={cancelling ? <CircularProgress size={16} color='inherit' /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
          data-testid='pd-cancel-courier-confirm'
        >
          {cancelling ? 'Cancelling…' : 'Cancel delivery'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelCourierDialog;
